import {useTranslation} from '@aboutyou/i18n';
import {LocationType} from '@aboutyou/router';
import {styled} from '@linaria/react';
import React, {useRef} from 'react';
import {COLORS} from '../../../constants/colors';
import {desktopAndUpLinaria} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {ImageType} from '../../../types/Image';
import {Button} from '../../atoms/Buttons/Button';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {ColorLabel} from '../../atoms/ColorLabel/ColorLabel';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {UnstyledLinkLinaria} from '../../atoms/UnstyledLink/UnstyledLinkLinaria';
import {ProductImage} from '../../molecules/ProductImage/ProductImage';
import {ProductColorOptionsProps} from '../ProductColorOptions/ProductColorOptions';
import {useProductColorOptionsPreview} from '../ProductColorOptionsPreview/utils/useProductColorOptionsPreview';
import {
  PRODUCT_COLOR_OPTIONS_OPEN_STATE_KEY,
  useProductColorOptionsGrid,
} from './utils/useProductColorOptionsGrid';

export const SHOULD_TRACK_PRODUCT_SELECTION_ADP =
  '@aboutyou/ui-components::PRODUCT_SELECTION_TRACKING';

interface ProductColorOptionsGridProps {
  className?: string;
  'data-testid'?: string;
  colorOptions: ProductColorOptionsProps['options'];
  setPreviewImage?: (previewImage?: ImageType) => void;
  setImageGridInactive?: (isActive: boolean) => void;
  onGridCollapse?: VoidFunction;
  onGridExpand?: VoidFunction;
  disablePreviewImage?: boolean;
  uniqueKey?: string;
  trackingData?: {
    productId: number;
  };
}

const NUMBER_OF_COLORS_IN_COLLAPSE_MODE = 12;
const COLOR_WIDTHS = [60, 120];
const COLOR_BREAKPOINTS = [{columns: 6}];

export const ProductColorOptionsGrid: React.FC<
  ProductColorOptionsGridProps
> = ({
  className,
  'data-testid': dataTestId,
  colorOptions,
  setPreviewImage,
  onGridCollapse,
  onGridExpand,
  uniqueKey = '',
}) => {
  const {t} = useTranslation();
  const colorGridRef = useRef<HTMLDivElement>(null);

  const {
    activeColorIndex,
    activeName,
    handleColorOptionMouseEnter,
    handleColorOptionMouseLeave,
  } = useProductColorOptionsPreview({
    colorOptions,
    setPreviewImage,
  });

  const {
    shouldShowCondensedView,
    isExpanded,
    colors,
    expandGrid,
    collapseGrid,
  } = useProductColorOptionsGrid({
    uniqueKey,
    colorOptions,
    activeColorIndex,
    numberOfColorsInCollapseMode: NUMBER_OF_COLORS_IN_COLLAPSE_MODE,
    onGridExpand,
    onGridCollapse,
  });

  return (
    <Wrapper className={className} data-testid={dataTestId}>
      {activeName && (
        <ColorLabel
          data-testid={dataTestId}
          label={t('Colour')}
          name={activeName}
        />
      )}
      <ColorsGrid
        ref={colorGridRef}
        onMouseLeave={handleColorOptionMouseLeave}
        data-testid={`${dataTestId}Wrapper`}
      >
        {(shouldShowCondensedView
          ? colors.slice(0, NUMBER_OF_COLORS_IN_COLLAPSE_MODE - 1)
          : colors
        ).map((color, index) => {
          const {path, image, isActive, isSoldOut, onClick, name} = color;

          const [pathname, search] = path.split('?');
          const searchParams = new URLSearchParams(search);

          const locationWithState = {
            pathname,
            search: searchParams.toString(),
            state: {
              [`${PRODUCT_COLOR_OPTIONS_OPEN_STATE_KEY}${uniqueKey}`]:
                isExpanded,
              [SHOULD_TRACK_PRODUCT_SELECTION_ADP]: true,
            },
          } as LocationType;

          return (
            <ColorWrapper
              key={color.path}
              data-testid={
                isSoldOut
                  ? `soldOutColorOption${index}`
                  : `${dataTestId}Option${index}`
              }
              data-active={isActive}
              to={locationWithState}
              replace
              forcePushBehaviour
              onClick={onClick}
              onMouseEnter={handleColorOptionMouseEnter({
                isActive,
                name,
                image: {
                  ...image,
                  isSoldOut,
                },
              })}
              aria-label={t('Go to article detail page variant')}
            >
              {isSoldOut ? (
                <>
                  <ProductImageSoldOut
                    padded
                    src={image.url}
                    widths={COLOR_WIDTHS}
                    breakpoints={COLOR_BREAKPOINTS}
                    alt={image.description}
                    ratio="1:1"
                  />
                  <SoldOutIcon
                    data-testid="soldOutColorOptionMailIcon"
                    asset={GenericIconName.Mail}
                    size={'SS'}
                  />
                </>
              ) : (
                <ProductImage
                  padded
                  src={image.url}
                  widths={COLOR_WIDTHS}
                  breakpoints={COLOR_BREAKPOINTS}
                  alt={image.description}
                  ratio="1:1"
                />
              )}
            </ColorWrapper>
          );
        })}
        {shouldShowCondensedView && (
          <ShowMoreButton
            data-testid={`${dataTestId}-expand`}
            onClick={expandGrid}
            aria-label={t('Show more')}
          >
            +{colors.length - NUMBER_OF_COLORS_IN_COLLAPSE_MODE + 1}
          </ShowMoreButton>
        )}
      </ColorsGrid>
      {isExpanded && (
        <ShowLessButton
          data-testid={`${dataTestId}-collapse`}
          onClick={collapseGrid}
        >
          {t('Show less variants')}
        </ShowLessButton>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  ${desktopAndUpLinaria(`width: 100%`)};
`;

const ColorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
  width: 100%;
  margin-top: 12px;

  ${desktopAndUpLinaria(`gap: 8px`)};
`;

const colorWrapperStyles = `
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 6px;
  border-radius: 4px;
  background-color: ${COLORS.ProductImageBackground};
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};

  &[data-active='true'] {
    border: 1px solid ${COLORS.Black};
  }
`;

const ColorWrapper = styled(UnstyledLinkLinaria)`
  ${colorWrapperStyles};
`;

const ShowMoreButton = styled(UnstyledButton)`
  border: none;
  color: ${COLORS.Black};
  ${colorWrapperStyles};
`;

const ProductImageSoldOut = styled(ProductImage)`
  opacity: 0.2;
`;

const SoldOutIcon = styled(Icon)`
  position: absolute;
`;

const ShowLessButton = styled(Button)`
  color: ${COLORS.Black};
  font-weight: ${FONT_WEIGHTS.light};
  background-color: ${COLORS.Grey5};
  border-radius: 4px;
  width: 100%;
  margin-top: 8px;
  padding: 8px 0;
`;
